import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, API, notifyError } from "@/lib/api";
import { toast } from "sonner";
import {
  Key, Lightning, FileArrowDown, Copy, CheckCircle, X, Plus, Trash, Prohibit,
  PlugsConnected, Warning, LinkSimple, Code, PaperPlaneTilt,
} from "@phosphor-icons/react";

const CONNECTORS = [
  { id: "sap", name: "SAP", tagline: "S/4HANA · Concur", status: "beta", color: "#0FAAFF" },
  { id: "infor", name: "Infor", tagline: "M3 · SunSystems · ION API", status: "waitlist", color: "#D50037" },
  { id: "flowforma", name: "FlowForma", tagline: "Power Automate · SharePoint", status: "beta", color: "#00B67A" },
  { id: "xero", name: "Xero", tagline: "Accounting sync", status: "waitlist", color: "#13B5EA" },
  { id: "quickbooks", name: "QuickBooks", tagline: "Intuit accounting", status: "waitlist", color: "#2CA01C" },
  { id: "netsuite", name: "NetSuite", tagline: "Oracle ERP", status: "waitlist", color: "#B10D28" },
];

const EVENTS = ["receipt.created", "receipt.deleted", "grant_expense.created", "grant.created", "certificate.issued"];

export default function Integrations() {
  const [keys, setKeys] = useState([]);
  const [hooks, setHooks] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [newKey, setNewKey] = useState(null); // one-time reveal
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [keyLabel, setKeyLabel] = useState("");
  const [copied, setCopied] = useState("");

  const [hookForm, setHookForm] = useState({ url: "", events: ["receipt.created", "grant_expense.created"], label: "" });
  const [showHookModal, setShowHookModal] = useState(false);

  const [reqConnector, setReqConnector] = useState(null);
  const [reqForm, setReqForm] = useState({ company_name: "", notes: "" });

  const [vatForm, setVatForm] = useState({ start: `${new Date().getFullYear()}-01-01`, end: `${new Date().getFullYear()}-12-31` });
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const applyVatPreset = (preset) => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth(); // 0-based
    if (preset === "bimonthly") {
      const startM = Math.floor(m / 2) * 2;            // 0,2,4,6,8,10
      setVatForm({ start: iso(new Date(y, startM, 1)), end: iso(new Date(y, startM + 2, 0)) });
    } else if (preset === "quarter") {
      const startM = Math.floor(m / 3) * 3;            // 0,3,6,9
      setVatForm({ start: iso(new Date(y, startM, 1)), end: iso(new Date(y, startM + 3, 0)) });
    } else if (preset === "ytd") {
      setVatForm({ start: `${y}-01-01`, end: iso(now) });
    }
  };
  const [vatBasis, setVatBasis] = useState("invoice");
  const [emailingVat, setEmailingVat] = useState(false);
  const emailVat3 = async () => {
    setEmailingVat(true);
    try {
      const r = await api.post("/exports/vat3/email", { period_start: vatForm.start, period_end: vatForm.end, basis: vatBasis });
      if (r.data.emailed) toast.success(`VAT3 CSV emailed to ${r.data.to}`);
      else toast.message(`Couldn't email ${r.data.to || "accountant"}`, {
        description: r.data.error === undefined ? "No accountant on file — invite one first."
          : "Email not sent — verify your Resend sender domain, then try again.", duration: 8000,
      });
    } catch (e) { notifyError(e); } finally { setEmailingVat(false); }
  };

  const load = useCallback(async () => {
    const [k, h, w] = await Promise.all([
      api.get("/api-keys"), api.get("/webhooks"), api.get("/integrations/waitlist"),
    ]);
    setKeys(k.data); setHooks(h.data); setWaitlist(w.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const createKey = async () => {
    try {
      const r = await api.post("/api-keys", { label: keyLabel || "default" });
      setNewKey(r.data);
      setKeyLabel("");
      setShowKeyModal(false);
      load();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  };

  const revokeKey = async (id) => {
    if (!confirm("Revoke this API key? External systems using it will lose access.")) return;
    await api.delete(`/api-keys/${id}`); load();
  };

  const copyText = async (id, text) => {
    try { await navigator.clipboard.writeText(text); }
    catch { window.prompt("Copy:", text); }
    setCopied(id); setTimeout(() => setCopied(""), 2000);
  };

  const createHook = async () => {
    try {
      await api.post("/webhooks", hookForm);
      setHookForm({ url: "", events: ["receipt.created", "grant_expense.created"], label: "" });
      setShowHookModal(false);
      load();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  };

  const deleteHook = async (id) => {
    if (!confirm("Delete this webhook?")) return;
    await api.delete(`/webhooks/${id}`); load();
  };

  const requestConnector = async () => {
    try {
      await api.post("/integrations/waitlist", { connector: reqConnector.id, ...reqForm });
      setReqConnector(null); setReqForm({ company_name: "", notes: "" });
      load();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  };

  const download = (url, filename) => {
    const a = document.createElement("a"); a.href = url; a.download = filename; a.target = "_blank";
    document.body.appendChild(a); a.click(); a.remove();
  };

  const waitlistedIds = new Set(waitlist.map(w => w.connector));

  return (
    <div className="space-y-10">
      <div>
        <div className="eyebrow mb-2">Integrations Hub</div>
        <h1 className="font-display text-4xl">Interop with anything</h1>
        <p className="text-white/60 text-sm mt-2 max-w-3xl">
          Push compliance data to SAP, Infor, FlowForma, Xero, QuickBooks or any middleware (Zapier, Make, Power Automate) via API keys,
          webhooks and vendor-shaped CSV/XML exports. All calls are logged for GDPR audit.
        </p>
      </div>

      {/* API Keys */}
      <Section
        icon={Key}
        eyebrow="Machine-to-machine"
        title="API Keys"
        subtitle="Grant read access to your compliance data. Send in header X-API-Key."
        action={<button onClick={() => setShowKeyModal(true)} className="btn-gold" data-testid="btn-new-key"><Plus size={16} weight="bold"/> Generate</button>}
      >
        {keys.length === 0 ? (
          <Empty text="No API keys yet."/>
        ) : (
          <div className="space-y-2">
            {keys.map(k => (
              <div key={k.key_id} className="glass rounded-xl p-4 flex flex-wrap items-center gap-4" data-testid={`key-${k.key_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{k.label}</div>
                  <div className="text-xs text-white/50 mono mt-1">{k.prefix}••••••••••••••••</div>
                </div>
                <div className="text-xs text-white/50">
                  {k.last_used_at ? <>Last used {k.last_used_at.slice(0, 10)}</> : <>Never used</>}
                </div>
                <span className={`badge-${k.revoked ? "fail" : "pass"} px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider`}>{k.revoked ? "Revoked" : "Active"}</span>
                {!k.revoked && (
                  <button onClick={() => revokeKey(k.key_id)} className="btn-danger text-xs" data-testid={`btn-revoke-key-${k.key_id}`}>
                    <Prohibit size={12}/> Revoke
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Endpoint reference */}
        <div className="mt-4 glass rounded-xl p-5">
          <div className="eyebrow mb-3">External API endpoints</div>
          <div className="space-y-2 mono text-xs">
            <EndpointRow method="GET" path="/api/external/v1/receipts" />
            <EndpointRow method="GET" path="/api/external/v1/grants" />
            <EndpointRow method="GET" path="/api/external/v1/transactions" />
          </div>
          <div className="mt-3 text-xs text-white/50">Auth: <span className="mono">X-API-Key: pc360_...</span></div>
        </div>
      </Section>

      {/* Webhooks */}
      <Section
        icon={Lightning}
        eyebrow="Push notifications"
        title="Webhooks"
        subtitle="POST signed events to your URL when data changes. HMAC-SHA256 signature in X-PerCen360-Signature header."
        action={<button onClick={() => setShowHookModal(true)} className="btn-gold" data-testid="btn-new-hook"><Plus size={16} weight="bold"/> Add webhook</button>}
      >
        {hooks.length === 0 ? (
          <Empty text="No webhooks configured."/>
        ) : (
          <div className="space-y-2">
            {hooks.map(h => (
              <div key={h.webhook_id} className="glass rounded-xl p-4" data-testid={`hook-${h.webhook_id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{h.label || "Webhook"}</div>
                    <div className="mono text-xs text-white/60 truncate">{h.url}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {h.events.map(e => <span key={e} className="text-[10px] uppercase tracking-wider bg-white/5 border border-white/10 px-2 py-0.5 rounded-full">{e}</span>)}
                  </div>
                  <div className="text-xs text-white/50">
                    {h.last_status != null ? <>Last: <span className="mono">{h.last_status}</span></> : "Never fired"}
                  </div>
                  <button onClick={() => deleteHook(h.webhook_id)} className="text-white/40 hover:text-[#E63946]" data-testid={`btn-del-hook-${h.webhook_id}`}><Trash size={14}/></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Vendor CSV exports */}
      <Section
        icon={FileArrowDown}
        eyebrow="Standardised downloads"
        title="Vendor exports"
        subtitle="Drop directly into your ERP or finance system. Column headers match each platform."
      >
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          <ExportCard title="SAP Concur"           desc="Standard expense import"       filename="percen360_sap_concur.csv" url={`${API}/exports/sap-concur.csv`} onDownload={download} testId="btn-export-sap"/>
          <ExportCard title="Infor SunSystems / M3" desc="Journal with VAT codes"        filename="percen360_infor.csv"      url={`${API}/exports/infor.csv`}      onDownload={download} testId="btn-export-infor"/>
          <ExportCard title="FlowForma"            desc="Power Automate / SharePoint"    filename="percen360_flowforma.csv"  url={`${API}/exports/flowforma.csv`}  onDownload={download} testId="btn-export-flowforma"/>
          <ExportCard title="Oracle NetSuite"      desc="Journal entry import"          filename="percen360_netsuite.csv"    url={`${API}/exports/netsuite.csv`}   onDownload={download} testId="btn-export-netsuite"/>
          <ExportCard title="Workday"              desc="Expense report import"          filename="percen360_workday.csv"     url={`${API}/exports/workday.csv`}    onDownload={download} testId="btn-export-workday"/>
          <ExportCard title="Sage"                 desc="Sage 50 / 200 accounting"       filename="percen360_sage.csv"        url={`${API}/exports/sage.csv`}       onDownload={download} testId="btn-export-sage"/>
          <ExportCard title="Xero"                 desc="Bills / invoice import"        filename="percen360_xero.csv"        url={`${API}/exports/xero.csv`}       onDownload={download} testId="btn-export-xero"/>
          <ExportCard title="QuickBooks"           desc="3-column bank CSV"             filename="percen360_quickbooks.csv"  url={`${API}/exports/quickbooks.csv`} onDownload={download} testId="btn-export-quickbooks"/>
          <ExportCard title="Unit4 Business World" desc="Public sector / education"      filename="percen360_unit4.csv"       url={`${API}/exports/unit4.csv`}      onDownload={download} testId="btn-export-unit4"/>
          <ExportCard title="Agresso / CGI"        desc="Local authority format"         filename="percen360_agresso.csv"     url={`${API}/exports/agresso.csv`}    onDownload={download} testId="btn-export-agresso"/>
        </div>
        <div className="mt-4 text-xs text-white/50 flex items-center gap-2">
          <LinkSimple size={13}/> Share <a href="/compatibility" target="_blank" rel="noreferrer" className="text-[#FFB020] underline">percen360.com/compatibility</a> in your grant applications — a public one-pager listing every supported platform.
        </div>
      </Section>

      {/* Grant report templates */}
      <Section
        icon={FileArrowDown}
        eyebrow="Grant body templates"
        title="Grant utilisation reports"
        subtitle="Pre-formatted CSVs matching Enterprise Ireland, Arc Hub, Horizon Europe and EIT Health reporting shapes."
      >
        <GrantTemplatePicker download={download}/>
      </Section>

      {/* VAT3 */}
      <Section
        icon={FileArrowDown}
        eyebrow="Irish Revenue"
        title="VAT3 return skeleton"
        subtitle="Draft XML with T1–T4 aggregated from your captured VAT. Final submission still requires ROS signing."
      >
        <div className="glass rounded-xl p-5 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div className="md:col-span-4 flex flex-wrap items-center gap-2">
            <span className="text-[11px] uppercase tracking-wider text-white/40 mr-1">Quick period</span>
            <button type="button" onClick={() => applyVatPreset("bimonthly")} data-testid="vat-preset-bimonthly" className="btn-ghost text-xs py-1">This bi-monthly</button>
            <button type="button" onClick={() => applyVatPreset("quarter")} data-testid="vat-preset-quarter" className="btn-ghost text-xs py-1">This quarter</button>
            <button type="button" onClick={() => applyVatPreset("ytd")} data-testid="vat-preset-ytd" className="btn-ghost text-xs py-1">Year to date</button>
          </div>
          <div className="md:col-span-4 flex flex-wrap items-center gap-2">
            <span className="text-[11px] uppercase tracking-wider text-white/40 mr-1">Sales VAT basis</span>
            {[["invoice", "Invoice basis"], ["cash", "Cash basis"]].map(([id, label]) => (
              <button key={id} type="button" onClick={() => setVatBasis(id)} data-testid={`vat-basis-${id}`}
                className={`text-xs px-2.5 py-1 rounded-lg border transition ${vatBasis === id ? "border-[#FFB020] text-[#FFB020] bg-[#FFB020]/10" : "border-white/12 text-white/55 hover:border-white/25"}`}>{label}</button>
            ))}
            <span className="text-[11px] text-white/35">{vatBasis === "cash" ? "VAT counted when invoices are paid" : "VAT counted when invoices are issued"}</span>
          </div>
          <div>
            <label>Period start</label>
            <input type="date" value={vatForm.start} onChange={e => setVatForm({...vatForm, start: e.target.value})} data-testid="input-vat-start"/>
          </div>
          <div>
            <label>Period end</label>
            <input type="date" value={vatForm.end} onChange={e => setVatForm({...vatForm, end: e.target.value})} data-testid="input-vat-end"/>
          </div>
          <div className="md:col-span-2 flex flex-col sm:flex-row gap-2">
            <button
              onClick={() => download(`${API}/exports/vat3.xml?period_start=${vatForm.start}&period_end=${vatForm.end}&basis=${vatBasis}`, `vat3_${vatForm.start}_${vatForm.end}.xml`)}
              className="btn-gold flex-1 justify-center"
              data-testid="btn-export-vat3"
            >
              <FileArrowDown size={16} weight="fill"/> VAT3 XML
            </button>
            <button
              onClick={() => download(`${API}/exports/vat3.csv?period_start=${vatForm.start}&period_end=${vatForm.end}&basis=${vatBasis}`, `vat3_${vatForm.start}_${vatForm.end}.csv`)}
              className="btn-ghost flex-1 justify-center"
              data-testid="btn-export-vat3-csv"
            >
              <FileArrowDown size={16} weight="fill"/> VAT3 CSV
            </button>
            <button
              onClick={emailVat3}
              disabled={emailingVat}
              className="btn-ghost flex-1 justify-center"
              data-testid="btn-email-vat3"
            >
              <PaperPlaneTilt size={16} weight="fill"/> {emailingVat ? "Sending…" : "Email accountant"}
            </button>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 text-xs text-white/50">
          <Warning size={13} color="#FFB020"/> Working/backup documents only. The official VAT3 must be filed via Revenue ROS. Emailing the CSV needs a verified Resend sender domain.
        </div>
      </Section>

      {/* Connectors */}
      <Section
        icon={PlugsConnected}
        eyebrow="Enterprise connectors"
        title="One-click connectors"
        subtitle="Fast-track live sync with your ERP. We'll build the connector with you — join the priority list."
      >
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {CONNECTORS.map(c => {
            const requested = waitlistedIds.has(c.id);
            return (
              <div key={c.id} className="glass rounded-xl p-5 flex flex-col gap-4" data-testid={`connector-${c.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-lg flex items-center justify-center font-display font-bold text-lg"
                      style={{ background: `${c.color}18`, border: `1px solid ${c.color}55`, color: c.color }}>
                      {c.name[0]}
                    </div>
                    <div>
                      <div className="font-display">{c.name}</div>
                      <div className="text-[10px] uppercase tracking-widest text-white/45 mt-1">{c.tagline}</div>
                    </div>
                  </div>
                  <span className="badge-warn px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wider font-semibold">{c.status}</span>
                </div>
                <div className="text-xs text-white/50 leading-relaxed">
                  Live sync coming. In the meantime, use the CSV export or API key above.
                </div>
                <button
                  onClick={() => { setReqConnector(c); setReqForm({ company_name: "", notes: "" }); }}
                  className={requested ? "btn-ghost text-xs justify-center" : "btn-gold text-xs justify-center"}
                  disabled={requested}
                  data-testid={`btn-connector-${c.id}`}
                >
                  {requested ? <><CheckCircle size={13} weight="fill" color="#00A86B"/> On the list</> : <><LinkSimple size={13}/> Request early access</>}
                </button>
              </div>
            );
          })}
        </div>
      </Section>

      {/* New API key reveal modal */}
      <AnimatePresence>
        {newKey && (
          <ModalShell title="Save this key now" subtitle="This is the only time you'll see it. Store it in your integration platform." onClose={() => setNewKey(null)} testId="new-key-modal">
            <div className="rounded-xl bg-[#FFB020]/10 border border-[#FFB020]/40 p-4 mb-4 text-xs text-white/80 flex gap-3">
              <Warning size={16} weight="fill" color="#FFB020" className="shrink-0"/>
              <span>PerCen360 does not store the raw key — only a hash. If you lose it, revoke and generate a new one.</span>
            </div>
            <label>API key</label>
            <div className="flex gap-2">
              <input readOnly value={newKey.api_key} onFocus={e => e.target.select()} className="mono text-xs" data-testid="input-new-key"/>
              <button onClick={() => copyText("new-key", newKey.api_key)} className="btn-gold text-xs shrink-0" data-testid="btn-copy-new-key">
                {copied === "new-key" ? <><CheckCircle size={12} weight="fill"/> Copied</> : <><Copy size={12}/> Copy</>}
              </button>
            </div>
            <div className="mt-6 rounded-lg bg-black/40 border border-white/10 p-4">
              <div className="eyebrow mb-2">Try it</div>
              <pre className="mono text-[11px] text-white/70 whitespace-pre-wrap break-all">{`curl -H "X-API-Key: ${newKey.api_key}" \\\n  ${API}/external/v1/receipts`}</pre>
            </div>
            <div className="flex justify-end mt-6">
              <button onClick={() => setNewKey(null)} className="btn-gold" data-testid="btn-close-new-key">I saved it</button>
            </div>
          </ModalShell>
        )}
      </AnimatePresence>

      {/* New key modal (label input) */}
      <AnimatePresence>
        {showKeyModal && (
          <ModalShell title="Generate API key" subtitle="Give it a label so you can recognise it later." onClose={() => setShowKeyModal(false)} testId="new-key-label-modal">
            <label>Label</label>
            <input value={keyLabel} onChange={e => setKeyLabel(e.target.value)} placeholder="e.g. Zapier integration" autoFocus data-testid="input-key-label"/>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowKeyModal(false)} className="btn-ghost">Cancel</button>
              <button onClick={createKey} className="btn-gold" data-testid="btn-create-key">Generate</button>
            </div>
          </ModalShell>
        )}
      </AnimatePresence>

      {/* Webhook modal */}
      <AnimatePresence>
        {showHookModal && (
          <ModalShell title="Add webhook" subtitle="We'll POST signed events to this URL." onClose={() => setShowHookModal(false)} testId="hook-modal">
            <div className="space-y-4">
              <div>
                <label>Label</label>
                <input value={hookForm.label} onChange={e => setHookForm({...hookForm, label: e.target.value})} placeholder="e.g. Zapier / n8n / Make" data-testid="input-hook-label"/>
              </div>
              <div>
                <label>Endpoint URL</label>
                <input type="url" value={hookForm.url} onChange={e => setHookForm({...hookForm, url: e.target.value})} placeholder="https://hooks.zapier.com/…" required data-testid="input-hook-url"/>
              </div>
              <div>
                <label>Subscribe to events</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {EVENTS.map(ev => {
                    const on = hookForm.events.includes(ev);
                    return (
                      <button key={ev} type="button"
                        onClick={() => setHookForm(f => ({ ...f, events: on ? f.events.filter(x => x !== ev) : [...f.events, ev] }))}
                        className={`text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-full transition ${on ? "bg-[#FFB020]/15 border border-[#FFB020]/40 text-[#FFB020]" : "bg-white/5 border border-white/10 text-white/60"}`}
                        data-testid={`event-${ev}`}
                      >
                        {ev}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-3 border-t border-white/5">
                <button onClick={() => setShowHookModal(false)} className="btn-ghost">Cancel</button>
                <button onClick={createHook} className="btn-gold" data-testid="btn-save-hook" disabled={!hookForm.url}>Save webhook</button>
              </div>
            </div>
          </ModalShell>
        )}
      </AnimatePresence>

      {/* Connector waitlist modal */}
      <AnimatePresence>
        {reqConnector && (
          <ModalShell
            title={`Request ${reqConnector.name} connector`}
            subtitle="We'll reach out to scope your integration and put you on the priority list."
            onClose={() => setReqConnector(null)}
            testId="connector-modal"
          >
            <div className="space-y-4">
              <div>
                <label>Company / entity name</label>
                <input value={reqForm.company_name} onChange={e => setReqForm({...reqForm, company_name: e.target.value})} placeholder="e.g. PerCen Technologies Ltd" data-testid="input-req-company"/>
              </div>
              <div>
                <label>What do you need synced?</label>
                <textarea rows={3} value={reqForm.notes} onChange={e => setReqForm({...reqForm, notes: e.target.value})} placeholder="e.g. Push receipts nightly to S/4HANA GL account 62100" data-testid="input-req-notes"/>
              </div>
              <div className="flex justify-end gap-3 pt-3 border-t border-white/5">
                <button onClick={() => setReqConnector(null)} className="btn-ghost">Cancel</button>
                <button onClick={requestConnector} className="btn-gold" data-testid="btn-submit-req">Join priority list</button>
              </div>
            </div>
          </ModalShell>
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------- helpers ----------
function Section({ icon: Icon, eyebrow, title, subtitle, action, children }) {
  return (
    <section>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0 mt-1">
            <Icon size={18} weight="duotone" color="#FFB020"/>
          </div>
          <div>
            <div className="eyebrow">{eyebrow}</div>
            <h2 className="font-display text-2xl mt-1">{title}</h2>
            {subtitle && <p className="text-white/55 text-sm mt-1 max-w-2xl">{subtitle}</p>}
          </div>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function Empty({ text }) {
  return <div className="glass rounded-xl p-8 text-center text-white/45 text-sm">{text}</div>;
}

function EndpointRow({ method, path }) {
  return (
    <div className="flex items-center gap-3">
      <span className="px-2 py-0.5 rounded bg-[#00A86B]/15 border border-[#00A86B]/30 text-[#00A86B] text-[10px] font-bold">{method}</span>
      <span className="text-white/80">{path}</span>
    </div>
  );
}

function ExportCard({ title, desc, filename, url, onDownload, testId }) {
  return (
    <div className="glass rounded-xl p-5 flex flex-col gap-3">
      <div>
        <div className="font-display text-lg">{title}</div>
        <div className="text-xs text-white/50 mt-1">{desc}</div>
      </div>
      <div className="text-[10px] text-white/40 mono truncate">{filename}</div>
      <button onClick={() => onDownload(url, filename)} className="btn-ghost text-sm justify-center mt-auto" data-testid={testId}>
        <FileArrowDown size={14}/> Download CSV
      </button>
    </div>
  );
}

function GrantTemplatePicker({ download }) {
  const [grants, setGrants] = useState([]);
  const [grantId, setGrantId] = useState("");
  const [template, setTemplate] = useState("enterprise-ireland");

  useEffect(() => {
    (async () => { const r = await api.get("/grants"); setGrants(r.data); if (r.data[0]) setGrantId(r.data[0].grant_id); })();
  }, []);

  const templates = [
    { id: "enterprise-ireland", label: "Enterprise Ireland" },
    { id: "arc-hub", label: "Arc Hub / ICT Cohort" },
    { id: "horizon-europe", label: "Horizon Europe" },
    { id: "eit-health", label: "EIT Health" },
    { id: "generic", label: "Generic utilisation report" },
  ];

  const go = () => {
    if (!grantId) { alert("Create a grant first."); return; }
    const url = `${API}/exports/grant-report/${grantId}.csv?template=${template}`;
    const name = `percen360_${template}_grant_report.csv`;
    download(url, name);
  };

  return (
    <div className="glass rounded-xl p-5 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
      <div>
        <label>Grant</label>
        <select value={grantId} onChange={e => setGrantId(e.target.value)} data-testid="select-grant-template">
          {grants.length === 0 && <option value="">No grants yet — create one first</option>}
          {grants.map(g => <option key={g.grant_id} value={g.grant_id}>{g.programme} — {g.name}</option>)}
        </select>
      </div>
      <div>
        <label>Template</label>
        <select value={template} onChange={e => setTemplate(e.target.value)} data-testid="select-template-body">
          {templates.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
      </div>
      <div className="md:col-span-2">
        <button onClick={go} className="btn-gold w-full justify-center" data-testid="btn-download-grant-template" disabled={!grantId}>
          <FileArrowDown size={14}/> Download report
        </button>
      </div>
    </div>
  );
}

function ModalShell({ title, subtitle, onClose, children, testId }) {
  return (
    <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto">
      <motion.div initial={{y:20,opacity:0}} animate={{y:0,opacity:1}} exit={{y:20,opacity:0}}
        className="glass rounded-2xl w-full max-w-xl my-8 p-8" data-testid={testId}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="eyebrow mb-1">Integrations</div>
            <div className="font-display text-2xl">{title}</div>
            {subtitle && <div className="text-white/55 text-sm mt-1">{subtitle}</div>}
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X size={20}/></button>
        </div>
        {children}
      </motion.div>
    </motion.div>
  );
}
